import { useMemo } from 'react';
import { identity, keyBy, partition, range, sum, toLower, zip } from 'lodash';
import { gql } from '@apollo/client';
import styled from 'styled-components';
import { useFarmName, useStartYear, useUserRole } from '../../utils/hooks';
import { useSubsidySwitchContext } from '../../contexts/SubsidySwitchContext';
import { useFilterContext } from '../../contexts/FilterContext';
import useFarmOrSectionGroupData from '../../hooks/useFarmOrSectionGroupData';
import ItemsTable from '../tables/ItemsTable';
import {
  asPercentage,
  numberAsCurrencyWithCents,
  numberAsDynamicRoundedDecimal,
} from '../../utils/formatters';
import { CropDisplayFragment } from '../../queries';
import {
  CropAggregate,
  EconomicSummary,
  OperationAggregate,
  RevenueStreamAggregate,
} from '../../types/graphql';
import { ASSIGNMENTS } from '../../constants/operations';
import { alphanumericSort } from '../../utils/utils';
import { LONG_AREA_UNITS, YIELD_UNITS_BY_DEFAULT } from '../../constants/units';
import { useUnits, useUnitsMap } from '../../contexts/UnitContext';
import { UnitConverter, UnitConverterMap } from '../../types/units';
import { isDirectOperated } from '../../utils/operations';
import { isCropFilterValid } from '../../utils/filters';

const SectionHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  border-bottom: 1px solid var(--border-color);
  margin: 0 0.5rem var(--cell-padding);
  height: 38.5px; // Prevent header from shifting when CSV button loads
`;

interface SummaryItem {
  metric: string;
  data: [];
}

interface UnsubsidizedCropAggregate extends CropAggregate {
  unsubsidizedEconomicSummary?: EconomicSummary;
}

interface EconomicSummaryTableProps {
  cropAggregates: CropAggregate[];
  economicSummary: EconomicSummary;
  queryMsg: Nullable<string>;
  unsubsidizedCropAggregates?: UnsubsidizedCropAggregate[];
  unsubsidizedEconomicSummary?: EconomicSummary;
}

const EconomicSummaryTableFragment = gql`
  ${CropDisplayFragment}
  fragment EcologicalSummaryFields on EcologicalSummary {
    carbonTonsByYear
    cumulativeAcreageByYear
    cumulativeTreesByYear
    treeCountByYear
    treesLostByYear
    treesPlantedByYear
    treesReplantedByYear
  }
  fragment EconomicSummaryTableFragment on SectionsAggregate {
    ecologicalSummary {
      ...EcologicalSummaryFields
    }
    cropAggregates {
      crop {
        ...CropDisplayFields
      }
      ecologicalSummary {
        ...EcologicalSummaryFields
      }
      revenueStreamAggregates {
        revenueStream {
          id
          name
          filledPercent
          filledUnitCost
        }
        revenueByYear
      }
    }
  }
`;

const negateArray = (arr: []) => arr?.map((num: number) => -num);

const getSubsidyArray = (
  unsubsidizedArray: Nullable<[]>,
  subsidizedArray: Nullable<[]>,
) => {
  return zip(
    unsubsidizedArray,
    subsidizedArray,
  ).map(([unsubsidizedCost, subsidizedCost]) =>
    unsubsidizedCost == null || subsidizedCost == null
      ? 0
      : unsubsidizedCost - subsidizedCost,
  );
};

const mergeCropAggregates = (
  cropAggregates1: CropAggregate[] = [],
  cropAggregates2: CropAggregate[] = [],
  unsubsidizedCropAggregates: UnsubsidizedCropAggregate[] = [],
) => {
  const aggregatesMap = keyBy(cropAggregates2, 'crop.id');
  const unsubsidizedAggregatesMap = keyBy(
    unsubsidizedCropAggregates,
    'crop.id',
  );
  return cropAggregates1.map((cropAggregate) => ({
    ...cropAggregate,
    ...aggregatesMap[cropAggregate.crop.id],
    unsubsidizedEconomicSummary:
      unsubsidizedAggregatesMap[cropAggregate.crop.id]
        ?.unsubsidizedEconomicSummary,
  }));
};

const mergeHoursOfLaborByYear = (operationAggregates: OperationAggregate[]) =>
  zip(...operationAggregates.map((opAgg) => opAgg.hoursOfLaborByYear)).map(sum);

const percentTreesLostByYear = (
  treeCountByYear: number[],
  treesLostByYear: number[],
) => {
  return zip(treeCountByYear, treesLostByYear).map(
    ([treeCount, treesLost]) => ((treesLost || 0) / (treeCount || 1)) * 100,
  );
};

const getColumns = (years: number, startYear: number) => [
  { displayString: 'Metric', accessor: 'metric' },
  ...range(years).map((yearIndex) => ({
    displayString: `${startYear + yearIndex}`,
    accessor: (item: SummaryItem) => item.data?.[yearIndex] || 0,
    format: numberAsDynamicRoundedDecimal,
  })),
];

const getSummaryExpenseItems = (
  metric: string,
  includeSubsidy: boolean,
  unsubsidizedExpense: Nullable<[]>,
  expense: [],
) => [
  ...(includeSubsidy && unsubsidizedExpense != null
    ? [
        {
          metric: `${metric} (unsubsidized)`,
          data: negateArray(unsubsidizedExpense),
          format: numberAsCurrencyWithCents,
        },
        {
          metric: `${metric} (subsidy)`,
          data: getSubsidyArray(unsubsidizedExpense, expense),
          format: numberAsCurrencyWithCents,
        },
      ]
    : []),
  {
    metric,
    data: negateArray(expense),
    format: numberAsCurrencyWithCents,
  },
];

const getSummaryItems = (
  data: UnsubsidizedCropAggregate,
  acreageUnits: UnitConverter,
  includeSubsidy: boolean = false,
) => {
  const {
    ecologicalSummary,
    economicSummary,
    unsubsidizedEconomicSummary,
  } = data;
  const {
    carbonTonsByYear,
    cumulativeAcreageByYear,
    cumulativeTreesByYear,
    treeCountByYear,
    treesLostByYear,
    treesPlantedByYear,
    treesReplantedByYear,
  } = ecologicalSummary;
  const {
    capexCostByYear,
    cumulativeGrossIncome,
    geneticsCostByYear,
    grossIncomeEbit,
    operationCostByYear,
    revenueByYear,
  } = economicSummary;
  const {
    capexCostByYear: unsubsidizedCapexCostByYear,
    geneticsCostByYear: unsubsidizedGeneticsCostByYear,
    operationCostByYear: unsubsidizedOperationCostByYear,
  } = unsubsidizedEconomicSummary || {};
  const { displayUnit, transformToUnit = identity } = acreageUnits;
  return [
    { metric: 'Cumulative trees planned', data: cumulativeTreesByYear },
    { metric: 'Trees planted in year', data: treesPlantedByYear },
    { metric: 'Trees replanted in year', data: treesReplantedByYear },
    { metric: 'Trees remaining', data: treeCountByYear },
    {
      metric: 'Trees lost in year (%)',
      data: percentTreesLostByYear(treeCountByYear, treesLostByYear),
      format: asPercentage,
    },
    {
      metric: `Cumulative ${displayUnit}`,
      data: cumulativeAcreageByYear.map(transformToUnit),
    },
    { metric: 'Cumulative tons CO2', data: carbonTonsByYear },
    {
      metric: 'Revenue',
      data: revenueByYear,
      format: numberAsCurrencyWithCents,
    },
    ...getSummaryExpenseItems(
      'Operating expenses',
      includeSubsidy,
      unsubsidizedOperationCostByYear,
      operationCostByYear,
    ),
    ...getSummaryExpenseItems(
      'Genetic stock capital expenses',
      includeSubsidy,
      unsubsidizedGeneticsCostByYear,
      geneticsCostByYear,
    ),
    ...getSummaryExpenseItems(
      'Site infrastructure capital expenses',
      includeSubsidy,
      unsubsidizedCapexCostByYear,
      capexCostByYear,
    ),
    {
      metric: 'Gross income',
      data: grossIncomeEbit,
      format: numberAsCurrencyWithCents,
    },
    {
      metric: 'Cumulative gross income',
      data: cumulativeGrossIncome,
      format: numberAsCurrencyWithCents,
    },
  ];
};

const getRevenueItems = (revenueStreamAggregates: RevenueStreamAggregate[]) =>
  revenueStreamAggregates.flatMap(({ revenueStream, revenueByYear }) => [
    {
      metric: `${revenueStream.name} (% yield)`,
      data: revenueStream.filledPercent,
      format: asPercentage,
    },
    {
      metric: `${revenueStream.name} (unit price)`,
      data: revenueStream.filledUnitCost,
      format: numberAsCurrencyWithCents,
    },
    {
      metric: `${revenueStream.name} (revenue)`,
      data: revenueByYear,
      format: numberAsCurrencyWithCents,
    },
  ]);

const getLaborItems = (
  operationAggregates: OperationAggregate[],
  includeDevLabor: boolean,
) => {
  const [ownerOpAggregates, devOpAggregates] = partition(
    operationAggregates.filter(({ operation }) => isDirectOperated(operation)),
    ({ operation }) => operation.assignment === ASSIGNMENTS.OWNER,
  );
  return [
    {
      metric: 'Labor hours (operated by owner)',
      data: mergeHoursOfLaborByYear(ownerOpAggregates),
    },
    ...(includeDevLabor
      ? [
          {
            metric: 'Labor hours (operated by developer)',
            data: mergeHoursOfLaborByYear(devOpAggregates),
          },
        ]
      : []),
  ];
};

const getOpExSubsidyArray = (
  category: string,
  economicSummary: EconomicSummary,
  unsubsidizedEconomicSummary?: EconomicSummary,
) => {
  if (!unsubsidizedEconomicSummary) return [];
  const categoryCost =
    economicSummary[`${toLower(category)}Opex` as keyof EconomicSummary];
  const unsubsidizedCategoryCost =
    unsubsidizedEconomicSummary[
      `${toLower(category)}Opex` as keyof EconomicSummary
    ];
  return getSubsidyArray(unsubsidizedCategoryCost, categoryCost);
};

const getOpExItems = (
  economicSummary: EconomicSummary,
  unsubsidizedEconomicSummary?: EconomicSummary,
  includeSubsidy: boolean = false,
) => {
  const mainEconomicSummary =
    includeSubsidy && unsubsidizedEconomicSummary
      ? unsubsidizedEconomicSummary
      : economicSummary;
  return ['Installation', 'Management', 'Harvest', 'Marketing'].flatMap(
    (category) => [
      {
        metric: `${category} cost (owner assigned)`,
        data: negateArray(
          mainEconomicSummary[`owner${category}Opex` as keyof EconomicSummary],
        ),
        format: numberAsCurrencyWithCents,
      },
      {
        metric: `${category} cost (developer assigned)`,
        data: negateArray(
          mainEconomicSummary[`dev${category}Opex` as keyof EconomicSummary],
        ),
        format: numberAsCurrencyWithCents,
      },
      ...(includeSubsidy && unsubsidizedEconomicSummary != null
        ? [
            {
              metric: `${category} cost (unsubsidized total)`,
              data: negateArray(
                unsubsidizedEconomicSummary[
                  `${toLower(category)}Opex` as keyof EconomicSummary
                ],
              ),
              format: numberAsCurrencyWithCents,
            },
            {
              metric: `${category} cost (subsidy)`,
              data: getOpExSubsidyArray(
                category,
                economicSummary,
                unsubsidizedEconomicSummary,
              ),
              format: numberAsCurrencyWithCents,
            },
          ]
        : []),
      {
        metric: `${category} cost (total)`,
        data: negateArray(
          economicSummary[`${toLower(category)}Opex` as keyof EconomicSummary],
        ),
        format: numberAsCurrencyWithCents,
      },
    ],
  );
};

const getItems = (
  areaUnits: UnitConverter,
  yieldUnitsMap: UnitConverterMap,
  includeDevLabor: boolean,
  includeSubsidy: boolean,
) => (data: UnsubsidizedCropAggregate) => {
  const {
    crop,
    economicSummary,
    operationAggregates = [],
    revenueStreamAggregates = [],
    unsubsidizedEconomicSummary,
    yieldAggregates = [],
  } = data;
  const summaryItems = getSummaryItems(data, areaUnits, includeSubsidy);
  const yieldItems = yieldAggregates.map(({ yield: yld, yieldByYear }) => {
    const { displayUnit, transformToUnit = identity } =
      yieldUnitsMap[yld.unit as keyof UnitConverterMap] || {};
    return {
      metric: `Yield by year (${displayUnit})`,
      data: yieldByYear.map(transformToUnit),
    };
  });
  const revenueItems = getRevenueItems(revenueStreamAggregates);
  const laborItems = getLaborItems(operationAggregates, includeDevLabor);
  const operationItems = getOpExItems(
    economicSummary,
    unsubsidizedEconomicSummary,
    includeSubsidy,
  );
  const items =
    crop.id === 'All'
      ? summaryItems
      : [
          ...summaryItems,
          ...yieldItems,
          ...revenueItems,
          ...laborItems,
          ...operationItems,
        ];
  return (
    items
      // Only show rows where there is at least one non-zero/null value
      .filter((item) => item.data?.some((value) => !!value))
      .map((item) => ({ id: `${crop.id}-${item.metric}`, ...item }))
  );
};

const EconomicSummaryTable = ({
  cropAggregates,
  economicSummary,
  unsubsidizedCropAggregates,
  unsubsidizedEconomicSummary,
  queryMsg,
}: EconomicSummaryTableProps) => {
  const { shouldShowSubsidies } = useSubsidySwitchContext();
  const {
    state: { crop: cropFilter, years: yearsFilter },
  } = useFilterContext();
  const startYear = useStartYear();
  const farmName = useFarmName();
  const { isSuperadmin } = useUserRole();
  const areaUnits = useUnits(LONG_AREA_UNITS);
  const yieldUnitsMap = useUnitsMap(YIELD_UNITS_BY_DEFAULT);

  const { msg, data } = useFarmOrSectionGroupData(
    EconomicSummaryTableFragment,
    'EconomicSummaryTableQuery',
    'EconomicSummaryTableFragment',
  );

  const cols = getColumns(yearsFilter, startYear);
  const csvFileName = [
    'Economic Summary',
    ...(shouldShowSubsidies ? ['subsidies applied'] : []),
    farmName,
  ].join(' - ');

  const tableHeader = ({ csvButton }: { csvButton?: JSX.Element } = {}) => (
    <SectionHeader>
      <h2 className="my-2">Raw Dataset</h2>
      {csvButton}
    </SectionHeader>
  );

  const tableData = useMemo(() => {
    const allCrop = {
      crop: { id: 'All', name: 'All' },
      ecologicalSummary: data?.ecologicalSummary,
      economicSummary,
      unsubsidizedCropAggregates,
      unsubsidizedEconomicSummary,
    };
    const mergedCrops = mergeCropAggregates(
      cropAggregates,
      data?.cropAggregates,
      unsubsidizedCropAggregates,
    );
    const cropFilterValid = isCropFilterValid(data, cropFilter);
    const activeCrops = cropFilterValid
      ? mergedCrops.filter(({ crop }) => crop.name === cropFilter)
      : mergedCrops;
    activeCrops.sort((a, b) => alphanumericSort(a.crop.name, b.crop.name));
    const multipleCrops = !cropFilterValid && activeCrops.length > 1;
    return {
      cropAggregates: [...(multipleCrops ? [allCrop] : []), ...activeCrops],
    };
  }, [
    cropAggregates,
    cropFilter,
    data,
    economicSummary,
    unsubsidizedCropAggregates,
    unsubsidizedEconomicSummary,
  ]);

  const memoizedGetItems = useMemo(
    () => getItems(areaUnits, yieldUnitsMap, isSuperadmin, shouldShowSubsidies),
    [areaUnits, yieldUnitsMap, isSuperadmin, shouldShowSubsidies],
  );

  if (queryMsg || msg) {
    return (
      <>
        {tableHeader()}
        <span className="mx-2 text-muted">{queryMsg || msg}</span>
      </>
    );
  }

  return (
    <ItemsTable
      byCrop
      cols={cols}
      csvFileName={csvFileName}
      csvButtonProps={{
        color: 'primary',
        className: 'ml-4 mb-2',
        outline: false,
      }}
      data={tableData}
      // @ts-expect-error ItemsTable has not been migrated to TS
      getItems={memoizedGetItems}
      msg={msg}
      // @ts-expect-error ItemsTable has not been migrated to TS
      tableHeader={tableHeader}
    />
  );
};

export default EconomicSummaryTable;
